<template>
  <div v-if="cartStore.cart && cartStore.cart.items.length">
    <UiText size="lg" class="text-primary-600">{{ $t('cart.myCart') }}</UiText>
    <UiHeading type="h1" class="my-4">
      {{
        $t('cart.nbItems', {
          count: cartStore.getCartItemsTotal,
          price: formatPrice(
            locale as Locale,
            cartStore.cart.total_price.amount,
            cartStore.cart.total_price.currency_code,
          ),
        })
      }}
    </UiHeading>
    <div v-if="isMobile">
      <UiSlider id="cart-line-items">
        <div v-for="item in cartStore.getCartLineItems" :key="item.id" class="mt-6">
          <CartItem :item="item" />
        </div>
      </UiSlider>
    </div>
    <div v-for="item in cartStore.getCartLineItems" v-else :key="item.id" class="mt-10">
      <CartItem :item="item" />
      <UiDivider />
    </div>
    <UiButton
      v-if="isFromCartPage"
      id="cart-clear"
      variant="primary"
      type="neutral"
      class="mt-5"
      :icon="IconClose"
      icon-side="left"
      @click="isModalOpen = !isModalOpen"
    >
      {{ $t('cart.clearCart') }}
    </UiButton>
    <UiModal
      v-if="isFromCartPage"
      id="cart-clear"
      :is-open="isModalOpen"
      :toggle="(value: boolean) => (isModalOpen = value)"
      :close="() => (isModalOpen = false)"
      :title="$t('removeModal.clearAllText')"
      :confirm-label="$t('general.confirm')"
      :cancel-label="$t('general.cancel')"
      :confirm-click="clearCart"
      :cancel-click="() => (isModalOpen = false)"
    >
      <UiText type="p">{{ $t('removeModal.content') }}</UiText>
    </UiModal>
  </div>
  <div v-else-if="isFromCartPage" class="w-full rounded-xl bg-white p-8 md:m-auto md:p-20">
    <UiHeading type="h2" class="text-center">{{ $t('cart.empty') }}</UiHeading>
    <UiButton id="cart-continue-shopping" variant="primary" class="mt-6 w-full" @click="goTo('/')">
      {{ $t('general.continueShopping') }}
    </UiButton>
  </div>
</template>

<script setup lang="ts">
import { IconClose } from '#components';

import type { CartContentProps } from '~/components/Cart/types';
import type { CartItem } from '~/stores/cart/types';

import { useAlertStore } from '~/stores/alert/alertStore';
import { useAppStore } from '~/stores/app/appStore';
import { useCartStore } from '~/stores/cart/cartStore';
import { CartActionType } from '~/stores/cart/types';

const goTo = useGoTo();
const { isMobile } = useBreakpoints();
const { locale } = useI18n();
const {
  proxy: { dataLayer },
} = useScriptGoogleTagManager();

const cartStore = useCartStore();
const alertStore = useAlertStore();
const appStore = useAppStore();

const isLoading = ref<boolean>(false);
const isModalOpen = ref<boolean>(false);

const clearCart = (): void => {
  dataLayer.push({
    component: 'cart_content',
    event: 'button_click',
    value: 'clear_cart',
  });

  isLoading.value = true;

  if (!cartStore.cart) {
    isLoading.value = false;
    return;
  }

  cartStore
    .updateCart({
      actions: cartStore.cart.items.map((item: CartItem) => {
        return {
          id: item.id,
          type: CartActionType.REMOVE_ITEM,
        };
      }),
      country: appStore.currentCountryCode,
      locale: locale.value as Locale,
    })
    .then(() =>
      alertStore.add({
        text: 'cart.clearCartSuccessMessage',
        title: 'general.successTitle',
        type: 'positive',
      }),
    )
    .finally(() => (isLoading.value = false));
};

defineProps<CartContentProps>();
</script>
